// WP-07 / AUDIT-FIX shared-contract (P1-03) — error taxonomy (LLD-05 §4/§5/
// Appendix B): AppError -> BusinessError (expected 4xx: validation, clash,
// expired quote, invalid status) and UnexpectedError (5xx / network, carries
// the correlationId that ties the failure to the API logs). Features handle
// these classes, never raw Axios. The uniform error BODY shape + its parser
// are single-sourced in @steier/shared/contract (field parses the same way).
import { parseApiError, type ApiErrorBody } from "@steier/shared/contract";
import { t } from "../i18n/i18n";

export abstract class AppError extends Error {
  constructor(
    readonly httpStatus: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = new.target.name;
  }
}

/** Expected 4xx business outcome — surfaced inline, never retried. */
export class BusinessError extends AppError {
  constructor(
    httpStatus: number,
    code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(httpStatus, code, message);
  }
}

/** 5xx / network / protocol failure — retryable, traceable by correlationId. */
export class UnexpectedError extends AppError {
  constructor(
    httpStatus: number,
    code: string,
    readonly correlationId?: string,
  ) {
    super(
      httpStatus,
      code,
      t("errors.unexpected", { correlationId: correlationId ?? "n/a" }),
    );
  }
}

/** Uniform API error body — re-exported from the shared contract (P1-03). */
export type { ApiErrorBody };

interface AxiosLikeError {
  isAxiosError?: boolean;
  response?: {
    status: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
  message?: string;
}

/** Map any transport failure to the AppError taxonomy (LLD-05 §4 mapError). */
export function mapError(error: unknown): AppError {
  if (error instanceof AppError) return error;

  const err = error as AxiosLikeError;
  const response = err?.response;
  if (!response) {
    // Network / CORS / timeout — no server response at all.
    return new UnexpectedError(0, "NETWORK");
  }

  const parsed = parseApiError(response.status, response.data, response.headers);
  const correlationId = parsed.correlationId ?? undefined;

  if (response.status >= 500) {
    return new UnexpectedError(
      response.status,
      parsed.code ?? "INTERNAL",
      correlationId,
    );
  }
  return new BusinessError(
    response.status,
    parsed.code ?? "BUSINESS_ERROR",
    parsed.message ??
      t("errors.unexpected", { correlationId: correlationId ?? "n/a" }),
    (response.data ?? {}) as Record<string, unknown>,
  );
}
